// ── 마케터별 계약 — 데스크탑 패널 (2026-06-07) ─────────────────────────────
// contracts_by_person 분리. 마케터 데이터만 표시.
//
// L 사이즈 통일 디자인:
//   - border-t accent indigo (UnifiedCard 기본)
//   - 헤더: 라벨 + chevron 월 nav + 핵심값 (총 N장, text-xl indigo)
//   - 구분선 (border-t-indigo-100) 후 본문
//   - watermark 우측 하단 (FileSignature)

import React from 'react';
import { FileText, FileSignature } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useContractMonth } from './useContractMonth';
import { UnifiedCard } from './UnifiedCard';
import { ContractTable } from './contractTable/ContractTable';
import { accentText, accentBorder } from './widgetAccent';

// code·title·byMonthUrl(2026-10-19) — 회사별·통합 계약 위젯이 같은 패널을 쓴다(기본값 = 본사 위젯 그대로).
export function ContractByMarketerPanel({
  accentOverride, sortableId, onClick,
  code = 'contracts_by_marketer', title = '마케터별 계약', byMonthUrl,
  summaryCode, // 위젯 메뉴가 없는 직원 — 카드 요약 길로 그달 표(2026-10-24)
}) {
  const {
    year,
    month,
    setYearMonth,
    loading,
    marketerRows,
    productColumns,
    categoryColumns,
  } = useContractMonth(byMonthUrl, { summaryCode });

  const ym = `${year}-${String(month).padStart(2, '0')}`;
  const handleMonthChange = (next) => {
    if (!/^\d{4}-\d{2}$/.test(next)) return;
    const [y, m] = next.split('-').map(Number);
    setYearMonth(y, m);
  };

  // 총 장 수 — marketerRows 의 total 합 (weighted 기준), 천단위 콤마 적용
  const totalCount = (marketerRows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
  const totalText = totalCount.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
  const tone = accentOverride || 'indigo';

  return (
    <UnifiedCard
      code={code}
      title={title}
      icon={FileText}
      accent="indigo"
      accentOverride={accentOverride}
      size="L"
      monthNavigate
      month={ym}
      onMonthChange={handleMonthChange}
      sortableId={sortableId}
      sub={loading ? '불러오는 중...' : null}
      watermarkIcon={FileSignature}
      onClick={onClick}
    >
      {/* 헤더 핵심값: 총 N장 */}
      <div className="flex items-baseline justify-end gap-1 mb-2">
        <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">총</span>
        <span className={cn('text-xl font-black tabular-nums leading-none', accentText(tone))}>
          {totalText}
        </span>
        <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">장</span>
      </div>
      {/* 구분선 */}
      <div className={cn('border-t mx-1 my-1.5', accentBorder(tone))} />
      {/* 본문 */}
      <ContractTable
        rows={marketerRows}
        nameLabel="마케터"
        productColumns={productColumns}
        categoryColumns={categoryColumns}
      />
    </UnifiedCard>
  );
}
